import { apiPatch } from "../client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/query-keys";
import type { IQuestion, IQuestionChoice } from "../exam_papers/types";

// 문제 수정 요청 인터페이스
export interface IUpdateQuestionRequest {
  category_id?: string;
  category?: string;
  passage?: string;
  passage_id?: string | null; // shared passage ID
  question?: string;
  status?: string;
  choices?: IQuestionChoice[];
  correct_choice_id?: string;
  answer_type?: string;
  correct_text_answer?: string;
}

/**
 * 문제 수정 API 함수
 * @param id 수정할 문제 ID
 * @param data 수정할 문제 데이터
 * @returns API 응답
 */
export const apiUpdateQuestion = async (
  id: string,
  data: IUpdateQuestionRequest
) => {
  const response = await apiPatch<IQuestion>(`/questions/${id}`, data);
  return response;
};

/**
 * 문제 수정 React Query 훅
 * @returns 뮤테이션 객체
 */
export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateQuestionRequest }) =>
      apiUpdateQuestion(id, data),
    onSuccess: (response, variables) => {
      // 특정 문제 정보와 문제 목록 모두 갱신
      queryClient.invalidateQueries({
        queryKey: QueryKeys.question.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: QueryKeys.question.all });

      // 연관된 시험 데이터도 갱신 (문제가 수정되면 시험 상세 정보의 문제도 변경됨)
      const exam_id = response.data?.exam_id;
      if (exam_id) {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.exam.detail(exam_id),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.question.byExam(exam_id),
        });
      }
    },
  });
};

export default useUpdateQuestion;
